import {
  CalendarDays,
  ContactRound,
  FileText,
  ListChecks,
  NotebookPen,
  RotateCcw,
  ShieldCheck,
  TriangleAlert,
  X,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { Button } from '../../../ui/Button/Button'
import {
  REQUEST_LABELS,
  requestSource,
  statusTime,
} from '../action-request-presentation'
import type { PatientDetails } from '../conversations-model'
import type {
  InteractionAppointmentContext,
  InteractionInsuranceContext,
  InteractionPatientContext,
  InteractionSummary,
} from '../interaction-context-repository'
import type { InternalNotesRepository } from '../internal-notes-repository'
import type { ActionRequestsView } from '../use-action-requests'
import type { ContextState } from '../use-interaction-context'
import {
  appointmentNote,
  callerNeedCopy,
  conversationAccount,
  topicLabels,
} from '../handoff-presentation'
import { IntentBadge, INTENT_PRESENTATION } from './IntentBadge'
import { InternalNotesCard } from './InternalNotesCard'
import { useCloseOnEscape } from './use-close-on-escape'
import styles from '../ConversationsPage.module.css'

function DetailRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className={styles.detailRow}>
      <dt>{label}</dt>
      <dd>{value ?? 'Not available'}</dd>
    </div>
  )
}

export function formatAppointmentTime(timestamp: number, timeZone: string) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(new Date(timestamp))
  } catch {
    return 'Time unavailable'
  }
}

function ContextMessage({ children }: { children: string }) {
  return <p className={styles.contextMessage}>{children}</p>
}

function ContextSkeleton({ label }: { label: string }) {
  return (
    <div className={styles.contextSkeleton} role="status" aria-label={label}>
      <span />
      <span />
      <span />
    </div>
  )
}

function PatientCard({
  state,
}: {
  state: ContextState<InteractionPatientContext>
}) {
  if (state.status === 'loading') {
    return <ContextSkeleton label="Loading patient context" />
  }
  if (state.status === 'error' || state.status === 'idle') {
    return <ContextMessage>Patient context is unavailable.</ContextMessage>
  }
  const patient = state.value
  const relationship =
    patient.associations?.status === 'MATCHED'
      ? 'Validated patient associations'
      : patient.associations?.status === 'PARTIAL'
        ? 'Patient associations incomplete'
        : patient.providerLinkStatus === 'NEXHEALTH_LINK_RECORDED'
          ? 'Practice-system link not verified'
          : patient.providerLinkStatus === 'CONFLICTED'
            ? 'Patient linkage unavailable'
            : 'Known to Flio only'
  return (
    <>
      <div className={styles.contextIdentityKind}>
        <span>Flio contact</span>
        <span>{relationship}</span>
      </div>
      {patient.associations && (
        <div className={styles.contextPeople}>
          {patient.associations.patients.length > 1 && <p>Shared contact</p>}
          {patient.associations.patients.map((person) => (
            <p key={person.patientId}>{person.displayName}</p>
          ))}
          {patient.associations.status !== 'MATCHED' && (
            <ContextMessage>
              Patient associations are incomplete or unavailable.
            </ContextMessage>
          )}
        </div>
      )}
      <dl>
        <DetailRow label="Phone" value={patient.phone} />
        <DetailRow label="Email" value={patient.email} />
        <DetailRow label="Flio status" value={patient.engagementStatus} />
      </dl>
    </>
  )
}

type AppointmentView = {
  key: string
  scheduledAtMs: number
  clinicTimeZone: string
  status: 'SCHEDULED' | 'CONFIRMED'
  conflict: boolean
  syncedAtMs: number | null
  patientDisplayName: string | null
  appointmentTypeName: string | null
  providerName: string | null
  operatoryName: string | null
}

function dateParts(timestamp: number, timeZone: string) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).formatToParts(new Date(timestamp))
    const read = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? ''
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(new Date(timestamp))
    return {
      weekday: read('weekday'),
      month: read('month'),
      day: read('day'),
      time,
    }
  } catch {
    return null
  }
}

// Days 1-7 are re-read from the PMS daily; only an older sync is worth a note.
const SYNC_NOTE_AFTER_MS = 26 * 60 * 60 * 1000

function AppointmentItem({
  appointment,
  nowMs,
}: {
  appointment: AppointmentView
  nowMs: number
}) {
  const parts = dateParts(appointment.scheduledAtMs, appointment.clinicTimeZone)
  const facts = [
    ['Provider', appointment.providerName],
    ['Operatory', appointment.operatoryName],
    ['Type', appointment.appointmentTypeName],
  ].filter((fact): fact is [string, string] => Boolean(fact[1]))
  const label = formatAppointmentTime(
    appointment.scheduledAtMs,
    appointment.clinicTimeZone
  )
  return (
    <article className={styles.appointmentItem} aria-label={label}>
      <span className={styles.appointmentDate} aria-hidden="true">
        <small>{parts?.month}</small>
        <strong>{parts?.day}</strong>
        <small>{parts?.weekday}</small>
      </span>
      <div className={styles.appointmentBody}>
        <div className={styles.appointmentTop}>
          <strong>{parts?.time ?? label}</strong>
          <span
            className={styles.appointmentStatus}
            data-status={
              appointment.conflict
                ? 'conflict'
                : appointment.status.toLowerCase()
            }
          >
            {appointment.conflict
              ? 'Check in PMS'
              : appointment.status === 'CONFIRMED'
                ? 'Confirmed'
                : 'Scheduled'}
          </span>
        </div>
        {appointment.patientDisplayName ? (
          <p className={styles.appointmentPatient}>
            {appointment.patientDisplayName}
          </p>
        ) : null}
        {facts.length > 0 ? (
          <dl className={styles.appointmentFacts}>
            {facts.map(([name, value]) => (
              <div key={name}>
                <dt>{name}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {appointment.conflict ? (
          <p className={styles.appointmentProvenance}>
            The clinic’s latest PMS read for this day no longer lists it; it may
            have moved or been cancelled.
          </p>
        ) : appointment.syncedAtMs !== null &&
          nowMs - appointment.syncedAtMs > SYNC_NOTE_AFTER_MS ? (
          <p className={styles.appointmentProvenance}>
            Last synced from the PMS{' '}
            {formatAppointmentTime(
              appointment.syncedAtMs,
              appointment.clinicTimeZone
            )}
          </p>
        ) : null}
      </div>
    </article>
  )
}

function AppointmentCard({
  state,
}: {
  state: ContextState<InteractionAppointmentContext>
}) {
  if (state.status === 'loading') {
    return <ContextSkeleton label="Loading appointment context" />
  }
  if (state.status === 'error' || state.status === 'idle') {
    return <ContextMessage>Appointment context is unavailable.</ContextMessage>
  }
  if (state.value.evidenceStatus) {
    const { evidenceStatus } = state.value
    const nowMs = Date.now()
    const appointments: AppointmentView[] = (
      state.value.appointments ?? []
    ).map((item, index) => ({
      key: `${item.patientId}-${item.scheduledAtMs}-${index}`,
      scheduledAtMs: item.scheduledAtMs,
      clinicTimeZone: item.clinicTimeZone,
      status: item.status,
      // Older user-management builds: one outside its day's read stays flagged.
      conflict: item.pmsConflict ?? item.evidenceStatus === 'UNAVAILABLE',
      syncedAtMs:
        item.pmsObservedAtMs === undefined
          ? item.observedAtMs
          : item.pmsObservedAtMs,
      patientDisplayName: item.patientDisplayName,
      appointmentTypeName: item.appointmentTypeName,
      providerName: item.providerName,
      operatoryName: item.operatoryName,
    }))
    // These are the clinic's PMS appointments, valid as the PMS last showed
    // them; a day read later than the latest sync is not a warning. Only a
    // failed or overflowing read, or an appointment the PMS no longer lists,
    // is a problem to show.
    const lookup =
      state.value.pmsLookup ??
      (appointments.length > 0 || evidenceStatus === 'CURRENT'
        ? 'COMPLETE'
        : 'FAILED')
    const notice =
      evidenceStatus === 'OVERFLOW'
        ? 'This contact has more PMS appointment records than can be listed here. Check the PMS.'
        : lookup === 'FAILED'
          ? 'Appointment information is unavailable right now.'
          : null
    return (
      <div className={styles.appointmentList}>
        {notice ? (
          <p className={styles.appointmentNotice}>
            <TriangleAlert size={14} aria-hidden="true" />
            <span>{notice}</span>
          </p>
        ) : null}
        {lookup === 'NOT_LINKED' && appointments.length === 0 ? (
          <ContextMessage>
            This contact isn’t linked to a PMS patient, so PMS appointments
            can’t be shown.
          </ContextMessage>
        ) : null}
        {lookup === 'COMPLETE' &&
        evidenceStatus !== 'OVERFLOW' &&
        appointments.length === 0 ? (
          <>
            <ContextMessage>
              {evidenceStatus === 'CURRENT'
                ? 'No upcoming appointments in the PMS for the next 45 days.'
                : 'No upcoming appointments found in the PMS.'}
            </ContextMessage>
            {evidenceStatus === 'CURRENT' ? null : (
              <p className={styles.appointmentProvenance}>
                Based on Flio’s latest PMS sync.
              </p>
            )}
          </>
        ) : null}
        {appointments.length > 1 ? (
          <p className={styles.appointmentCount}>
            {appointments.length} upcoming
          </p>
        ) : null}
        {appointments.map((item) => (
          <AppointmentItem key={item.key} appointment={item} nowMs={nowMs} />
        ))}
      </div>
    )
  }
  const appointment = state.value.appointment
  if (!appointment) {
    return (
      <ContextMessage>No upcoming Flio-recorded appointment.</ContextMessage>
    )
  }
  return (
    <div className={styles.appointmentList}>
      <AppointmentItem
        nowMs={Date.now()}
        appointment={{
          key: 'nearest',
          scheduledAtMs: appointment.scheduledAtMs,
          clinicTimeZone: appointment.clinicTimeZone,
          status: appointment.status,
          conflict: false,
          syncedAtMs: null,
          patientDisplayName: null,
          appointmentTypeName: appointment.appointmentTypeName,
          providerName: appointment.providerName,
          operatoryName: appointment.operatoryName,
        }}
      />
    </div>
  )
}

function InsuranceCard({
  state,
}: {
  state: ContextState<InteractionInsuranceContext>
}) {
  if (state.status === 'loading') {
    return <ContextSkeleton label="Loading insurance context" />
  }
  if (state.status === 'error' || state.status === 'idle') {
    return <ContextMessage>Insurance context is unavailable.</ContextMessage>
  }
  return (
    <>
      <ContextMessage>No insurance information available.</ContextMessage>
      <p className={styles.contextProvenance}>
        Flio has no authoritative practice-system insurance record for this
        interaction.
      </p>
    </>
  )
}

// Confirms one reopening in place, like the status bar's picker does.
function ReopenConfirm({
  label,
  onCancel,
  onConfirm,
}: {
  label: string
  onCancel: () => void
  onConfirm: () => void
}) {
  const titleId = useId()
  const dialog = useRef<HTMLDivElement>(null)
  // Focus lands on the question, not an answer: a repeated key never confirms.
  useEffect(() => {
    dialog.current?.focus()
  }, [])
  useCloseOnEscape(onCancel)
  return (
    <div
      ref={dialog}
      className={styles.requestConfirm}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <strong id={titleId}>Reopen this {label.toLowerCase()}?</strong>
      <p>
        It returns to Needs action for every staff member. No message is sent to
        the patient.
      </p>
      <div className={styles.statusConfirmActions}>
        <Button variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={onConfirm}>
          Reopen
        </Button>
      </div>
    </div>
  )
}

// Every request this conversation raised, open or completed, with the one
// correction staff may make later: reopening a completion.
function RequestsCard({ actions }: { actions: ActionRequestsView }) {
  const { requests } = actions
  const [confirming, setConfirming] = useState<string | null>(null)
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({})
  const cancel = useCallback(() => {
    setConfirming((current) => {
      if (current) triggers.current[current]?.focus()
      return null
    })
  }, [])
  const unavailable = (message: string) => (
    <div className={styles.requestsUnavailable}>
      <ContextMessage>{message}</ContextMessage>
      <Button
        variant="ghost"
        type="button"
        onClick={() => void actions.reload()}
      >
        Try again
      </Button>
    </div>
  )
  if (requests.items.length === 0) {
    if (requests.status === 'loading') {
      return <ContextSkeleton label="Loading requests" />
    }
    return unavailable('This conversation’s requests couldn’t be loaded.')
  }
  return (
    <>
      {requests.status === 'error'
        ? unavailable(
            'These requests couldn’t be refreshed and may be out of date.'
          )
        : null}
      <ul className={styles.requestList}>
        {requests.items.map((item) => {
          const label = REQUEST_LABELS[item.requestType]
          const completed = item.status === 'RESOLVED'
          return (
            <li
              key={item.actionRequestId}
              className={styles.requestItem}
              data-status={completed ? 'completed' : 'open'}
            >
              <div className={styles.requestMain}>
                <strong>{label}</strong>
                <span>{requestSource(item)}</span>
              </div>
              <span
                className={styles.requestStatus}
                data-status={completed ? 'completed' : 'open'}
              >
                {completed ? 'Completed' : 'Open'}
              </span>
              {completed && item.resolution ? (
                <div className={styles.requestFooter}>
                  <span>
                    Completed {statusTime(item.resolution.resolvedAtMs)}
                  </span>
                  {actions.supported && item.reopenable ? (
                    <Button
                      ref={(element) => {
                        triggers.current[item.actionRequestId] = element
                      }}
                      className={styles.requestReopen}
                      variant="ghost"
                      type="button"
                      disabled={actions.busy !== null}
                      aria-label={`Reopen ${label}`}
                      aria-expanded={confirming === item.actionRequestId}
                      onClick={() =>
                        setConfirming((current) =>
                          current === item.actionRequestId
                            ? null
                            : item.actionRequestId
                        )
                      }
                    >
                      <RotateCcw size={13} aria-hidden="true" /> Reopen
                    </Button>
                  ) : null}
                  {confirming === item.actionRequestId ? (
                    <ReopenConfirm
                      label={label}
                      onCancel={cancel}
                      onConfirm={() => {
                        setConfirming(null)
                        void actions.reopen([item])
                      }}
                    />
                  ) : null}
                </div>
              ) : item.reopenedAtMs ? (
                <div className={styles.requestFooter}>
                  <span>Reopened {statusTime(item.reopenedAtMs)}</span>
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>
    </>
  )
}

// Long advisory text stays complete in the page; only its default height is
// bounded. The toggle appears only when the text actually overflows.
function ExpandableText({ text }: { text: string }) {
  const id = useId()
  const paragraph = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  useLayoutEffect(() => {
    const element = paragraph.current
    if (!element) return
    const measure = () =>
      setOverflowing(element.scrollHeight - element.clientHeight > 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [text, expanded])
  return (
    <>
      <p
        id={id}
        ref={paragraph}
        className={`${styles.clampedText} ${expanded ? styles.clampedOpen : ''}`}
      >
        {text}
      </p>
      {overflowing || expanded ? (
        <button
          type="button"
          className={styles.readMore}
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      ) : null}
    </>
  )
}

const CHANNEL_LABELS = { SMS: 'SMS', VOICE: 'Voice' } as const

function InteractionMeta({
  channel,
  atMs,
}: {
  channel: keyof typeof CHANNEL_LABELS
  atMs: number
}) {
  const sameYear = new Date(atMs).getFullYear() === new Date().getFullYear()
  return (
    <small className={styles.summaryEntryMeta}>
      {CHANNEL_LABELS[channel]} ·{' '}
      <time
        dateTime={new Date(atMs).toISOString()}
        title={new Intl.DateTimeFormat(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(atMs)}
      >
        {new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric',
          ...(sameYear ? {} : { year: 'numeric' }),
          hour: 'numeric',
          minute: '2-digit',
        }).format(atMs)}
      </time>
    </small>
  )
}

// Words for a summary that does not cover the latest communication.
function freshnessNote(status: InteractionSummary['freshness']['status']) {
  return status === 'GENERATING'
    ? 'This summary is being updated.'
    : status === 'FAILED'
      ? 'This summary couldn’t be updated. The conversation shows the latest messages.'
      : status === 'STALE'
        ? 'No summary is available for the latest conversation.'
        : null
}

// Only the summary of the latest meaningful communication is the discussion;
// every other summary stays one click away, so an earlier call's outcome (or a
// summary older than the latest call or text) never reads as the current state.
// Older user-management builds send only the joined discussion.
function Discussion({
  advisory,
  status,
}: {
  advisory: InteractionSummary['advisory']
  status: InteractionSummary['freshness']['status']
}) {
  if (!advisory.entries) {
    return advisory.discussion ? (
      <ExpandableText text={advisory.discussion} />
    ) : null
  }
  const current = advisory.entries.filter((entry) => entry.current)
  const others = advisory.entries.filter((entry) => !entry.current)
  const note = current.length === 0 ? freshnessNote(status) : null
  const othersAreEarlier = others.every((entry) =>
    current.every((lead) => entry.atMs <= lead.atMs)
  )
  return (
    <>
      {note ? <p className={styles.summaryPending}>{note}</p> : null}
      {current.map((entry) => (
        <div key={`${entry.channel}:${entry.atMs}`}>
          <InteractionMeta channel={entry.channel} atMs={entry.atMs} />
          <ExpandableText text={entry.text} />
        </div>
      ))}
      {others.length > 0 ? (
        <details className={styles.summaryEarlier}>
          <summary>
            {othersAreEarlier ? 'Earlier activity' : 'Other activity'} (
            {others.length})
          </summary>
          {others.map((entry) => (
            <div key={`${entry.channel}:${entry.atMs}`}>
              <InteractionMeta channel={entry.channel} atMs={entry.atMs} />
              <p>{entry.text}</p>
            </div>
          ))}
        </details>
      ) : null}
    </>
  )
}

function SummaryCard({ state }: { state: ContextState<InteractionSummary> }) {
  if (state.status === 'loading') {
    return <ContextSkeleton label="Loading conversation summary" />
  }
  if (state.status === 'error' || state.status === 'idle') {
    return <ContextMessage>Conversation summary is unavailable.</ContextMessage>
  }
  const { advisory, businessTruth, freshness } = state.value
  const latest = businessTruth.latestInteraction ?? null
  const hasDiscussion = advisory.entries
    ? advisory.entries.length > 0
    : advisory.discussion !== null
  const hasOutcome = businessTruth.appointmentStatus !== null
  const hasUnresolved = businessTruth.unresolvedActionRequestCount > 0
  // A verified booking is the outcome of the request it came from.
  const booked = latest?.intent === 'APPOINTMENT_BOOKED'
  const secondary = latest?.secondary ?? []
  const topics = topicLabels(latest?.topics)
  const account = latest
    ? conversationAccount(latest, latest.atMs)
    : { attempted: [], completed: [], outcomes: [] }
  const need = latest ? callerNeedCopy(latest) : null
  const appointment = latest
    ? appointmentNote(
        [latest.intent, ...secondary],
        latest.channel === 'VOICE' ? 'call' : 'conversation'
      )
    : null
  const hasContent =
    latest !== null ||
    advisory.intent !== null ||
    hasDiscussion ||
    hasOutcome ||
    hasUnresolved
  if (!hasContent) {
    const message =
      freshness.status === 'GENERATING'
        ? 'Conversation summary is being prepared.'
        : freshness.status === 'FAILED'
          ? 'The latest conversation summary is temporarily unavailable.'
          : 'Not enough supported interaction evidence to summarize yet.'
    return (
      <>
        <ContextMessage>{message}</ContextMessage>
        <p className={styles.contextProvenance}>
          Flio does not infer intent or outcomes from missing evidence.
        </p>
      </>
    )
  }
  // With entries, the discussion states its own coverage; otherwise (a summary
  // still to come, or an older user-management build) the note leads the card.
  const leadNote =
    advisory.entries && hasDiscussion ? null : freshnessNote(freshness.status)
  return (
    <div className={styles.summaryCard}>
      {leadNote ? <p className={styles.summaryWarning}>{leadNote}</p> : null}
      {latest ? (
        <div className={styles.summarySection}>
          <strong>Primary intent</strong>
          <div className={styles.summaryLatest}>
            <IntentBadge
              intent={booked ? 'APPOINTMENT_REQUEST' : latest.intent}
              prominent
            />
            <InteractionMeta channel={latest.channel} atMs={latest.atMs} />
          </div>
          {topics.length ? (
            <p className={styles.summaryFact}>Asked about {topics.join(', ')}</p>
          ) : null}
        </div>
      ) : null}
      {secondary.length ? (
        <div className={styles.summarySection}>
          <strong>Secondary requests</strong>
          <ul className={styles.summaryPlainList}>
            {secondary.map((intent) => (
              <li key={intent}>{INTENT_PRESENTATION[intent].label}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {account.attempted.length ? (
        <div className={styles.summarySection}>
          <strong>Actions attempted</strong>
          <ul className={styles.summaryPlainList}>
            {account.attempted.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {account.completed.length ? (
        <div className={styles.summarySection}>
          <strong>Actions completed</strong>
          <ul className={styles.summaryPlainList}>
            {account.completed.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {booked || account.outcomes.length ? (
        <div className={styles.summarySection}>
          <strong>Verified outcomes</strong>
          {booked ? <IntentBadge intent="APPOINTMENT_BOOKED" /> : null}
          {account.outcomes.length ? (
            <ul className={styles.summaryPlainList}>
              {account.outcomes.map((outcome) => (
                <li key={outcome}>{outcome}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {appointment ? (
        <div className={styles.summarySection}>
          <strong>Appointment</strong>
          <p className={styles.summaryFact}>{appointment}</p>
        </div>
      ) : null}
      {need ? (
        <div className={styles.summarySection}>
          <strong>Caller need</strong>
          <p className={styles.summaryFact}>
            {need.label}. {need.description}
          </p>
        </div>
      ) : null}
      {advisory.intent ? (
        <div className={styles.summarySection}>
          <strong>Intent</strong>
          <p>{advisory.intent}</p>
        </div>
      ) : null}
      {hasDiscussion ? (
        <div className={styles.summarySection}>
          <strong>Discussion</strong>
          <Discussion advisory={advisory} status={freshness.status} />
        </div>
      ) : null}
      {hasOutcome ? (
        <div className={styles.summarySection}>
          <strong>PMS appointment</strong>
          <p className={styles.summaryFact}>
            An upcoming appointment is{' '}
            {businessTruth.appointmentStatus?.toLowerCase()} in the clinic’s
            PMS.
          </p>
        </div>
      ) : null}
      {hasUnresolved ? (
        <div className={styles.summarySection}>
          <strong>Open requests</strong>
          <ul className={styles.summaryRequests}>
            {businessTruth.unresolvedActionRequestLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className={styles.contextProvenance}>
        Discussion is advisory. Intent, actions and outcomes come from Flio’s
        records; appointments come from the clinic’s PMS.
      </p>
    </div>
  )
}

export function PatientDetail({
  details,
  patientContext,
  appointmentContext,
  insuranceContext = { status: 'idle', value: null },
  summaryContext = { status: 'idle', value: null },
  onClose,
  tenantId,
  internalNotesRepository,
  aggregateId = null,
  actionRequests,
}: {
  details: PatientDetails
  patientContext: ContextState<InteractionPatientContext>
  appointmentContext: ContextState<InteractionAppointmentContext>
  insuranceContext?: ContextState<InteractionInsuranceContext>
  summaryContext?: ContextState<InteractionSummary>
  onClose: () => void
  tenantId: string
  internalNotesRepository?: InternalNotesRepository
  aggregateId?: string | null
  actionRequests?: ActionRequestsView
}) {
  const displayName =
    patientContext.status === 'ready'
      ? patientContext.value.displayName
      : details.displayName
  return (
    <div className={styles.patientDetail}>
      <div className={styles.detailHeader}>
        <Button
          className={styles.detailBack}
          variant="ghost"
          type="button"
          onClick={onClose}
        >
          <X size={17} aria-hidden="true" /> Close
        </Button>
        <div className={styles.detailIdentity}>
          <span className={styles.largeAvatar} aria-hidden="true">
            {displayName
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => Array.from(part)[0])
              .join('')
              .toUpperCase() || 'P'}
          </span>
          <div>
            <h2>{displayName}</h2>
          </div>
        </div>
      </div>

      <section className={styles.detailSection}>
        <h3>
          <ContactRound size={15} aria-hidden="true" /> Patient
        </h3>
        <PatientCard state={patientContext} />
      </section>
      <section className={styles.detailSection}>
        <h3>
          <FileText size={15} aria-hidden="true" /> Conversation summary
        </h3>
        <SummaryCard state={summaryContext} />
      </section>
      {actionRequests?.readable &&
      actionRequests.requests.status !== 'idle' &&
      (actionRequests.requests.status !== 'ready' ||
        actionRequests.requests.items.length > 0) ? (
        <section className={styles.detailSection}>
          <h3>
            <ListChecks size={15} aria-hidden="true" /> Requests
          </h3>
          <RequestsCard actions={actionRequests} />
        </section>
      ) : null}
      <section className={styles.detailSection}>
        <h3>
          <CalendarDays size={15} aria-hidden="true" /> Next appointment
        </h3>
        <AppointmentCard state={appointmentContext} />
      </section>
      <section className={styles.detailSection}>
        <h3>
          <ShieldCheck size={15} aria-hidden="true" /> Insurance
        </h3>
        <InsuranceCard state={insuranceContext} />
      </section>
      {internalNotesRepository && aggregateId ? (
        <section className={styles.detailSection}>
          <h3>
            <NotebookPen size={15} aria-hidden="true" /> Internal notes
          </h3>
          <InternalNotesCard
            tenantId={tenantId}
            aggregateId={aggregateId}
            repository={internalNotesRepository}
          />
        </section>
      ) : null}
    </div>
  )
}
